import React from 'react';
import PropTypes from 'prop-types';
import Card from './Card';
import { HEADER } from '../../constants';

/**
 * @description MobileCard
 *
 * @returns {<MobileCard />}
 */
class MobileCard extends React.Component {
  /**
   * Converts a property value from % or px to an integer of pixels.
   *
   * @param {String} value - Property value to convert.
   * @return {String} - Converted property value.
   */
  static getPropertyValue(value) {
    const windowHeight = MobileCard.getWindowHeight();
    const intValue = parseInt(value, 10);
    return value.indexOf('%') > -1 ? (intValue / 100) * windowHeight : intValue;
  }

  /**
   * Gets the current window height.
   *
   * @return {Number} - Window height.
   */
  static getWindowHeight() {
    return window.innerHeight;
  }

  constructor(props) {
    super(props);

    // State
    this.state = {
      touchStyle: {},
      moving: false,
      expanded: false,
    };

    // Local variables
    this.touchStart = 0;
    this.cardStart = 0;

    // Local constants
    this.minSwipeThreshold = 0.05;
    this.minSwipeCloseThreshold = 0.6;
    this.maxSwipeMoveDistance = 0.8;

    // Refs
    this.setCardRef = element => {
      this.card = element;
    };
    this.setBodyRef = element => {
      this.body = element;
    };

    // Event Handlers
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleCardClick = this.handleCardClick.bind(this);
  }

  /**
   * Unexpand the card whenever it's hidden / closed so that when the user re-opens the next card
   * it is not expanded.
   *
   * @param {Object} newProps - Incoming props.
   */
  componentWillReceiveProps(newProps) {
    if (newProps.hidden) this.setState({ expanded: false });
  }

  /**
   * Handles touchstart event.
   *
   * Sets the initial top property of the card, which is to be adjusted as the user moves their
   * touch point.
   *
   * @param {Object} e - Event object.
   */
  handleTouchStart(e) {
    const touchY = e.changedTouches[0].clientY;
    const top = MobileCard.getPropertyValue(
      window.getComputedStyle(this.card).getPropertyValue('top')
    );

    this.setState({
      touchStyle: {
        top: `${top}px`,
        transition: 'none',
      },
    });

    this.cardStart = top;
    this.touchStart = touchY;
  }

  /**
   * Handles touchmove event.
   *
   * Moves the card along with the touch. If the card body is not scrolled all the way to the top,
   * then do not move the card down until the card body has been scrolled back to the top of the
   * scrollable area.
   *
   * @param {Object} e - Event object.
   */
  handleTouchMove(e) {
    const touchY = e.changedTouches[0].clientY;
    const windowHeight = MobileCard.getWindowHeight();
    const maxSwipeMoveDistancePixels = this.maxSwipeMoveDistance * windowHeight;

    // If there is still scrollable room in the body card, make sure it is scrolled all the way up.
    if (this.body.scrollTop > 0 && this.state.expanded) {
      this.setState({
        moving: false,
      });

      // Change the new baseline from which to calculate the scrolled amount.
      this.touchStart = touchY;
      return;
    }

    // Calculate the new top of the card based on the diff from the start of the touch.
    const diff = this.touchStart - touchY;
    const newTop = Math.min(Math.max(this.cardStart - diff, HEADER), maxSwipeMoveDistancePixels);

    this.setState({
      touchStyle: {
        top: `${newTop}px`,
        transition: 'none',
      },
      moving: newTop !== HEADER,
    });
  }

  /**
   * Handles touchend event. Only changes the card expanded / collapsed state if the swiped amount
   * meets a minimum swipe threshold. Expands / collapses the card based on the swipe direction.
   *
   * If swiping down past a certain viewport threshold, then the card is closed.
   *
   * @param {Object} e - Event object.
   */
  handleTouchEnd(e) {
    const touchY = e.changedTouches[0].clientY;
    const diff = this.touchStart - touchY;
    const windowHeight = MobileCard.getWindowHeight();

    const minSwipeThresholdPixels = this.minSwipeThreshold * windowHeight;
    const minSwipeCloseThresholdPixels = this.minSwipeCloseThreshold * windowHeight;

    let expanded = null;

    if (Math.abs(diff) > minSwipeThresholdPixels) {
      if (touchY > minSwipeCloseThresholdPixels && diff < 0) {
        // Close the card if swiping down past a certain threshold.
        this.props.handleClose();
        expanded = false;
      } else {
        // Expand if swiping up, collapse if swiping down.
        expanded = diff > 0;
      }
    }

    this.setState({
      touchStyle: {},
      moving: false,
      expanded: expanded !== null ? expanded : this.state.expanded,
    });
  }

  /**
   * Expand card on click. Do not expand for outbound anchor links.
   */
  handleCardClick(e) {
    return e.target.nodeName !== 'A' && this.setState({ expanded: !this.state.expanded });
  }

  render() {
    const { moving, expanded } = this.state;
    const { hidden } = this.props;

    // Define card class names.
    const hiddenClass = hidden ? 'card--hidden' : '';
    const expandedClass = expanded ? 'card--expanded' : '';
    const movingClass = moving ? 'card--moving' : '';

    const cardClassNames = `card card--mobile ${hiddenClass} ${expandedClass} ${movingClass}`;

    return (
      <div
        className={cardClassNames}
        onClick={this.handleCardClick}
        onTouchMove={this.handleTouchMove}
        onTouchStart={this.handleTouchStart}
        onTouchEnd={this.handleTouchEnd}
        style={this.state.touchStyle}
        ref={this.setCardRef}
        role="presentation"
      >
        <Card {...this.props} bodyRef={this.setBodyRef} />
      </div>
    );
  }
}

MobileCard.propTypes = {
  hidden: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default MobileCard;
